/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useEffect, useRef } from 'react'
import { Card, Title, Text } from '@tremor/react'
import {
  ResponsiveContainer,
  AreaChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Area,
} from 'recharts'
import { CircularProgress, IconButton } from '@material-ui/core'
import GetAppIcon from '@material-ui/icons/GetApp'

import moment from 'config/moment'
import { useApiCall } from 'hooks/useApi'

import { useLoadingTracker } from '../../hooks/useLoadingTracker'
import { CustomCheckbox } from '../../components/CustomCheckbox'
import {
  ActiveUsersEvolutionByLevelData,
  ActiveUsersEvolutionByLevelParams,
  ActiveUsersEvolutionByLevelResponse,
  getActiveUsersEvolutionByLevel,
} from './api'
import { montserratStyle } from '../../styles'

interface LevelInfo {
  name: string
  level: number
}

// Colors used in custom CSS for `#area-chart-niveles`:
// level_1 -> #C9D296, level_2 -> #F29569, level_3 -> #E8B342
const LEVEL_COLOR_HEX: { [level: number]: string } = {
  1: '#C9D296',
  2: '#F29569',
  3: '#E8B342',
}

interface UniqueUsersEvolutionProps {
  // eslint-disable-next-line @typescript-eslint/member-delimiter-style
  period: { month: number; year: number }
  // eslint-disable-next-line react/require-default-props
  storeId?: number
  trigger: number
  // eslint-disable-next-line react/require-default-props
  onLoaded?: () => void
}

const downloadCSV = (data: ActiveUsersEvolutionByLevelData[], levels: LevelInfo[]) => {
  const headers = ['Mes', ...levels.map(level => level.name)]
  const csvRows = [
    headers,
    ...data.map(row => [row.month, ...levels.map(level => row[`level_${level.level}`] || 0)]),
  ]
  const csvContent = csvRows.map(e => e.join(',')).join('\n')
  const blob = new Blob([csvContent], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)

  const a = document.createElement('a')
  a.href = url
  a.download = 'evolucion_usuarios_unicos.csv'
  a.click()
  URL.revokeObjectURL(url)
}

const UniqueUsersEvolution: React.FC<UniqueUsersEvolutionProps> = ({
  period,
  storeId,
  trigger,
  onLoaded,
}) => {
  const [showAllUsers, setShowAllUsers] = useState(false)
  const [showRecurrent, setShowRecurrent] = useState(true)
  const [data, setData] = useState<ActiveUsersEvolutionByLevelData[] | null>(null)
  const [levels, setLevels] = useState<LevelInfo[]>([])
  const canFetchRef = useRef(false)
  const skipFilterFetchRef = useRef(true)
  const [statsCall, statsLoading] = useApiCall<
    ActiveUsersEvolutionByLevelParams,
    ActiveUsersEvolutionByLevelResponse
  >(getActiveUsersEvolutionByLevel)

  // Track loading state
  useLoadingTracker({ isLoading: statsLoading, componentId: 'UniqueUsersEvolution' })

  const fetchData = async (callOnLoaded = false) => {
    setLevels([])
    const userType = showRecurrent ? 'recurrent' : 'all'
    try {
      const response = await statsCall({
        endDate: moment()
          .year(period.year)
          .month(period.month)
          .endOf('month')
          .format('YYYY-MM-DD'),
        storeId,
        userType,
      })
      if (response?.data) {
        const { dates, levels: levelsData } = response.data
        setLevels(levelsData)
        setData(dates)
      }
    } finally {
      if (callOnLoaded && onLoaded) {
        onLoaded()
      }
    }
  }

  useEffect(() => {
    if (trigger > 0) {
      canFetchRef.current = true
      fetchData(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger])

  useEffect(() => {
    if (skipFilterFetchRef.current) {
      skipFilterFetchRef.current = false
      return
    }
    if (!canFetchRef.current) return
    fetchData(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showRecurrent, showAllUsers])

  return (
    <Card id="area-chart-niveles" className="w-full mt-8 p-6 bg-white rounded-2xl shadow">
      <div className="flex flex-row justify-between items-center mb-2">
        <div>
          <Title className="text-base font-bold" style={montserratStyle}>
            Evolución de usuarios únicos por nivel
          </Title>
          {data && (
            <div className="flex gap-4 mt-2">
              {levels.map(level => {
                const color = LEVEL_COLOR_HEX[level.level] || '#C9D296'

                return (
                  <div key={level.level} className="flex items-center gap-2">
                    <span className="w-4 h-4 rounded" style={{ backgroundColor: color }} />
                    <span className="text-xs font-semibold">{level.name}</span>
                  </div>
                )
              })}
            </div>
          )}
        </div>
        {data && (
          <div className="flex gap-4 items-center">
            <label className="flex items-center gap-2 text-xs font-medium">
              <CustomCheckbox
                checked={showAllUsers}
                onChange={e => {
                  const { checked } = e.target
                  if (checked) {
                    setShowAllUsers(true)
                    setShowRecurrent(false)
                  } else {
                    // Si intenta desmarcar, asegurar que el otro esté marcado
                    if (!showRecurrent) {
                      setShowRecurrent(true)
                    }
                    setShowAllUsers(false)
                  }
                }}
                className="w-4 h-4 rounded border border-gray-400"
              />
              Todos los usuarios
            </label>
            <label className="flex items-center gap-2 text-xs font-medium">
              <CustomCheckbox
                checked={showRecurrent}
                onChange={e => {
                  const { checked } = e.target
                  if (checked) {
                    setShowRecurrent(true)
                    setShowAllUsers(false)
                  } else {
                    // Si intenta desmarcar, asegurar que el otro esté marcado
                    if (!showAllUsers) {
                      setShowAllUsers(true)
                    }
                    setShowRecurrent(false)
                  }
                }}
                className="w-4 h-4 rounded border border-gray-400"
              />
              Usuarios recurrentes
            </label>
            <IconButton
              disabled={statsLoading}
              className="ml-2"
              aria-label="download"
              onClick={() => data && downloadCSV(data, levels)}
            >
              <GetAppIcon />
            </IconButton>
          </div>
        )}
      </div>
      {statsLoading || !data ? (
        <div className="flex items-center justify-center h-32">
          {statsLoading ? (
            <CircularProgress size={40} />
          ) : (
            <Text>No hay datos disponibles para mostrar en este momento.</Text>
          )}
        </div>
      ) : (
        <div className="w-full h-[300px] mb-6">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 10, right: 20, left: 10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis
                type="number"
                domain={[0, 100]}
                tickFormatter={(v: number) => `${v}%`}
                width={40}
              />
              <Tooltip formatter={(value: number) => `${value}%`} />
              {levels.map(level => (
                <Area
                  key={level.level}
                  type="monotone"
                  dataKey={`level_${level.level}`}
                  stackId={1}
                  // Do not force colors here; custom CSS in `backoffice/src/styles/tailwind.css`
                  // targets `#area-chart-niveles path[name="level_X"]` and sets the brand colors.
                  name={`level_${level.level}`}
                  stroke={LEVEL_COLOR_HEX[level.level] || '#C9D296'}
                  fillOpacity={0.4}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  )
}

export default UniqueUsersEvolution
